import type { Graphics } from 'pixi.js'
import { BOGGLE_SPECIAL_TILE_REGISTRY } from '~/boggle/config/specialTiles/registry'
import { resolveLiveCellDisplayKind } from '~/boggle/engine/specialTiles'

export type TileShapeKind = 'circle' | 'hexagon' | 'pentagon'

const HEXAGON_ROTATION_RADIANS = -Math.PI / 2
export const PENTAGON_ROTATION_RADIANS = -Math.PI / 2

export function getTileShapeKind(
  cell: { tags?: readonly string[] } | null | undefined,
  goldRushActive = false,
) {
  const displayKind = resolveLiveCellDisplayKind(cell, goldRushActive)
  if (displayKind) {
    return BOGGLE_SPECIAL_TILE_REGISTRY[displayKind].tileShape
  }

  return 'circle'
}

export function getRegularPolygonPoints(
  sides: number,
  radius: number,
  centerX = 0,
  centerY = 0,
  rotationRadians = HEXAGON_ROTATION_RADIANS,
) {
  return Array.from({ length: sides }, (_, index) => {
    const angle = rotationRadians + (Math.PI * 2 * index) / sides

    return {
      x: centerX + Math.cos(angle) * radius,
      y: centerY + Math.sin(angle) * radius,
    }
  })
}

/** CSS `clip-path: polygon(...)` for a regular polygon inscribed in a square box. */
export function regularPolygonClipPath(
  sides: number,
  rotationRadians = HEXAGON_ROTATION_RADIANS,
): string {
  const points = getRegularPolygonPoints(sides, 0.5, 0.5, 0.5, rotationRadians)
  const path = points
    .map(point => `${(point.x * 100).toFixed(3)}% ${(point.y * 100).toFixed(3)}%`)
    .join(', ')
  return `polygon(${path})`
}

function getPolygonSides(shape: Exclude<TileShapeKind, 'circle'>) {
  return shape === 'hexagon' ? 6 : 5
}

function getPolygonRotation(shape: Exclude<TileShapeKind, 'circle'>) {
  return shape === 'hexagon' ? HEXAGON_ROTATION_RADIANS : PENTAGON_ROTATION_RADIANS
}

export function drawTileShapePath(
  graphics: Graphics,
  shape: TileShapeKind,
  centerX: number,
  centerY: number,
  radius: number,
) {
  if (shape === 'circle') {
    graphics.circle(centerX, centerY, radius)
    return
  }

  const points = getRegularPolygonPoints(
    getPolygonSides(shape),
    radius,
    centerX,
    centerY,
    getPolygonRotation(shape),
  )
  const [firstPoint, ...restPoints] = points
  if (!firstPoint) {
    return
  }

  graphics.moveTo(firstPoint.x, firstPoint.y)
  for (const point of restPoints) {
    graphics.lineTo(point.x, point.y)
  }
  graphics.closePath()
}

function isPointInsidePolygon(
  x: number,
  y: number,
  points: ReadonlyArray<{ x: number; y: number }>,
) {
  let inside = false
  for (let index = 0, previous = points.length - 1; index < points.length; previous = index++) {
    const current = points[index]!
    const prior = points[previous]!
    const intersects =
      current.y > y !== prior.y > y &&
      x < ((prior.x - current.x) * (y - current.y)) / (prior.y - current.y) + current.x
    if (intersects) {
      inside = !inside
    }
  }
  return inside
}

export function isPointInsideTileShape(
  deltaX: number,
  deltaY: number,
  hitRadius: number,
  shape: TileShapeKind,
) {
  if (shape === 'circle') {
    return deltaX * deltaX + deltaY * deltaY <= hitRadius * hitRadius
  }

  const points = getRegularPolygonPoints(
    getPolygonSides(shape),
    hitRadius,
    0,
    0,
    getPolygonRotation(shape),
  )
  return isPointInsidePolygon(deltaX, deltaY, points)
}
